// File: src/components/Payroll/PayrollReportViewer.jsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchEmergencyStatus, isFeatureLocked } from '@/utils/emergencyLock';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useNavigate } from 'react-router-dom';

const PayrollReportViewer = () => {
  const [data, setData] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [message, setMessage] = useState('');

  const { canViewFinance } = useUserPermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canViewFinance) {
      navigate('/unauthorized');
    }
  }, [canViewFinance, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get('/api/payroll/summary');
        setData(res.data || []);
      } catch (err) {
        console.error('Error loading payroll data:', err);
      }
    };

    const checkLock = async () => {
      const status = await fetchEmergencyStatus();
      setIsLocked(isFeatureLocked(status, ['cyberattack', 'offline']));
    };

    fetchData();
    checkLock();
  }, []);

  const handleDownloadPDF = async () => {
    try {
      const res = await axios.get('/api/payroll/download-pdf', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'PayrollReport.pdf');
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error('PDF download failed:', err);
      setMessage('Error generating payroll PDF.');
    }
  };

  return (
    <div className="bg-neutral-800 p-6 rounded-2xl shadow-xl text-white">
      <h2 className="text-xl font-semibold mb-4">📊 Payroll Report</h2>

      {isLocked ? (
        <div className="bg-red-600 text-white p-3 rounded-lg font-medium text-center">
          🚫 Payroll view disabled due to Emergency Mode.
        </div>
      ) : (
        <>
          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="text-left border-b border-gray-600">
                <th className="py-2">Staff</th>
                <th>Total Hours</th>
                <th>Rate</th>
                <th>Pay (£)</th>
              </tr>
            </thead>
            <tbody>
              {data.map((staff, i) => (
                <tr key={i} className="border-b border-gray-700">
                  <td className="py-2">{staff.name}</td>
                  <td>{staff.total_hours}</td>
                  <td>£{staff.rate}</td>
                  <td>£{staff.total_pay}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            onClick={handleDownloadPDF}
            className="bg-blue-600 hover:bg-blue-700 p-2 px-6 rounded-xl font-semibold"
          >
            Download Payroll PDF
          </button>
        </>
      )}

      {message && (
        <p className="mt-4 text-yellow-400 font-medium">{message}</p>
      )}
    </div>
  );
};

export default PayrollReportViewer;
